/* eslint-disable react/no-multi-comp */
// @flow

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import { log, numberWithCommas } from '../../utils/jsUtils';
import { useQuery } from 'urql';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import {Elements, CardElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import StripeElementWrapper from './StripeElementWrapper';

// MUI imports
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Radio from '@mui/material/Radio';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Hidden from '@mui/material/Hidden';

// Services and components
import ContentfulProvider from '../../services/contentful';
import ContentfulPickPlanPage from './ContentfulPickPlanPage';
import ContentfuRenderPlanDesc from './ContentfuRenderPlanDesc';
import CreditCardForm from '../SharedComponents/CreditCardForm';
import SPBackend from '../../services/SPBackend';

// Redux selectors and actions
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { setAccountUsage } from '../../reduxStore/modules/user/actions';
import { setBilling } from '../../reduxStore/modules/billing/actions';

// Styles
import upgradePlanDialogStyles from './Styles/UpgradePlanDialog.Style';
import signupStyles from '../PublicCpts/Styles/Signup.Style';
import { createClasses } from '../../utils/stylesUtils';
import type { Plan } from '../../services/SPBackend';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PlansPicker = (props) => {
  const { selectPlan } = props;
  const theme = useTheme();
  const classes = createClasses(upgradePlanDialogStyles, theme);

  const [result, reexecuteQuery] = useQuery({
    query: `query ($id: String!) {
      marketingPage(id: $id) {
        slug
        browserTabTitle
        columnSectionsCollection {
          items {
            __typename
            ... on PricingSection {
              pricingTable
            }
          }
        }
      }
    }`,
    variables: {
      id: '2tWOj84ne25ZNtSYoDQHet' // Marketing Page that contains the Pricing Section.
    }
  });

  const { data, fetching, error } = result;
  console.log('[PlansHome] fetching, error: ', fetching, error, data);
  if (typeof data !== 'undefined') {
    console.log('[PlansHome] data.marketingPage.columnSectionsCollection: ', data.marketingPage.columnSectionsCollection);
  }

  return (
      <Grid sx={classes.contentCtr}
        item
        xs={12}
      >
      {
        !fetching && !error && data
        ? data.marketingPage.columnSectionsCollection.items.map((section, sectionIdx) => {
          return (
            section.__typename === 'PricingSection'
            ? <ContentfulPickPlanPage 
                key={`section-${sectionIdx}`}
                pricingTable={section.pricingTable}
                section={section}
                selectPlan={selectPlan}
              />
            : null
          );
        })
        : null
      }
      </Grid>
  );
};

const CheckoutForm = (props) => {
  const {
    planinfo,
    setOpenDialog,
    coupon,
    paymentMethods,
    defaultPaymentMethod,
    showAddNewCardForm,
    checkoutFlow,
    setCheckoutFlow
  } = props;
  
  // Use hooks
  const theme = useTheme();
  const classes = createClasses(upgradePlanDialogStyles, theme);
  const combinedClasses = createClasses({...upgradePlanDialogStyles, ...signupStyles}, theme);
  const dispatch = useDispatch();
  
  // Use selectors instead of useStore
  const user = useSelector(selectUser);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [chargeError, setChargeError] = useState('');
  const [selectedPMId, setSelectedPMId] = useState('');
  
  const stripe = useStripe();
  const elements = useElements();
  
  log('[CheckoutForm] classes: ', classes);
  
  const handleSubmit = async(ev) => {
    try {
      ev.preventDefault();
      setPaymentLoading(true);
      log('submit payment!', ev);

      log('[handleSubmit] planId: ', planinfo.id);
      if (!planinfo.id || typeof planinfo.id === 'undefined') {
        setChargeError('Please choose a plan first.');
        throw Error('Please choose a plan first.');
      }
      
      let selectedPM = null;
      if (selectedPMId !== '') {
        selectedPM = {
          id: selectedPMId
        };
      } else {

        // https://stripe.com/docs/stripe-js/reference#elements-get-element
        const cardElement = elements.getElement('card');
        log('[handleSubmit] elements(cardNumber): ', cardElement);
        let pmResult = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: cardholderName
          }
        });
        log('[handleSubmit] after createPaymentMethod. ');
        const { paymentMethod, error: pmError } = pmResult;
        if (pmError) {
          log('Stripe paymentMethod error: ', pmError);
          if (pmError.code === 'parameter_invalid_empty') {
            throw Error('All fields are required.');
          }
          throw Error(pmError.message);
        }
        
        log('resulting pmResult: ', paymentMethod);
        selectedPM = paymentMethod;
      }
      
      // Omit trialEnd param, so trial expires now.
      const paymentPayload = {
        paymentMethod: selectedPM,
        planId: planinfo.id
      };
      if (typeof coupon !== 'undefined' && coupon !== null) {
        paymentPayload.coupon = coupon.id;
      }
      SPBackend.chargePlan(paymentPayload, user)
        .then((response) => {
          setChargeError('');
          log('charge response: ', response);
          SPBackend.getAccount(user)
          .then(response => {
            log('[getAccount] response: ', response);
            const { data } = response;
            const { account, billing } = data;
            
            // Use modern Redux actions instead of direct dispatch
            dispatch(setAccountUsage(account.Usage));
            dispatch(setBilling(billing));
            
            setPaymentLoading(false);
            setCheckoutFlow((prev) => ({
              ...prev,
              currentStep: 2
            }));
            log('[chargePlan] updated checkoutflow');
          });
        })
        .catch(error => {
          log('[handleSubmit] charge error.response: ', error.response);
          setPaymentLoading(false);
          const data = error.response?.data || 'Plan upgrade failed. Please try again.';
          setChargeError(data);
        });
    } catch (e) {
      log('[handleSubmit] e.name: ', e.name);
      if (e.name === 'IntegrationError') {
        setChargeError('Missing card information');
      } else {
        log('[handleSubmit] error caught: ', e.name, e);
        setChargeError(e.message);
      }
      setPaymentLoading(false);
    }
  };

  log('[CheckoutForm] planinfo: ', planinfo);

  useEffect(() => {
    if (defaultPaymentMethod !== null) {
      setSelectedPMId(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod]);

  const handleSelectPaymentMethod = (pmId) => {
    log('[handleSelectPaymentMethod] pmId: ', pmId);
    setSelectedPMId(pmId);
  };

  return (
    <Grid container>
      <form sx={classes.pmFormCtr}
onSubmit={handleSubmit}
      >
        <Typography sx={{...classes.spaceTop}}
variant='body1'
        ><b>Payment method</b></Typography>
      {
        !showAddNewCardForm
        ? <List>
            <RadioGroup aria-label="gender"
              sx={classes.pmRadioGroup}
              color='primary'
              name="selectPaymentMethod"
              onChange={(e) => handleSelectPaymentMethod(e.target.value)}
              value={selectedPMId}
            >
      
              {
                paymentMethods.map((pm, i) => {
                  return (
                    <ListItem sx={classes.pmListItem}
                      key={`pm-${i}`}
                    >
                      <ListItemAvatar>
                        <FormControlLabel
                          control={<Radio color='primary'/>}
                          value={pm.id}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        onClick={() => handleSelectPaymentMethod(pm.id)}
                        primary={`${pm.card.brand.toUpperCase()} (${pm.card.last4}) - Expires ${pm.card.exp_month}/${pm.card.exp_year}`}
                        secondary={defaultPaymentMethod && pm.id === defaultPaymentMethod.id ?'(Default)':''}
                      />
                    </ListItem>
                  );
                })
              }
            </RadioGroup>
          </List>
        : null
      }
      {
        showAddNewCardForm
        ? <><Grid item
            xs={12}
            >
            <Typography sx={combinedClasses.formLabel}
    variant='body1'
            >{'Cardholder full name'}</Typography>
            <FormControl
              sx={{...combinedClasses.newInputForm}}
              variant="outlined"
            >
              <OutlinedInput
                sx={{...combinedClasses.newFormElt, ...combinedClasses.checkoutInputForm}}
                id='companyName'
                margin='dense'
                name='companyName'
                onChange={event => setCardholderName(event.target.value)}
                placeholder='Cardholder full name'
                type='text'
                value={cardholderName}
              />
            </FormControl>
          </Grid>
          <Grid item
            xs={12}
          >
            <Typography sx={combinedClasses.formLabel}
    variant='body1'
            >{'Card information'}</Typography>
            <StripeElementWrapper component={CardElement}
    id='card-elt-id'
    label=''
            />
          </Grid> 
        </>
        : null
      }
      {
        chargeError !== ''
        ? <Grid
            sx={combinedClasses.errorLabel}
            item
            xs={12}
          >{chargeError}</Grid>
        : null
      }

        <Box sx={classes.reviewItemCtr}>
          <Typography variant='body1'><b>Payment total</b></Typography>
          <Typography variant='body1'><b>{checkoutFlow.isPeriodYearly ? `$${numberWithCommas(checkoutFlow.planDef.yearly * 12)} per year` : `$${checkoutFlow.planDef.monthly} per month`}</b></Typography>
        </Box> 
        <Box sx={classes.ctaBtnCtr}>
          <Button
            color='primary'
            disabled={paymentLoading || (!showAddNewCardForm && (!planinfo || planinfo.product === ''))}
            style={{marginTop: 16}}
            type='submit'
            variant='contained'
          >{planinfo ? 'Subscribe' : 'Loading'}</Button>
        </Box>
      </form>
    </Grid>
  );
};

const CheckoutPlan = (props) => {
  console.log('[CheckoutPlan] props: ', props);
  const {
    setCheckoutFlow,
    checkoutFlow,
    setOpenDialog
  } = props;

  // Use hooks
  const theme = useTheme();
  const classes = createClasses(upgradePlanDialogStyles, theme);
  const dispatch = useDispatch();
  
  // Use selector instead of useStore
  const user = useSelector(selectUser);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [showAddNewCardForm, setShowAddNewCardForm] = useState(false);
  const [planInfo, setPlanInfo] = useState({
    nickname: '',
    active: true,
    amount: 0,
    currency: 'usd',
    interval: 'month',
    interval_count: 0,
    product: ''
  });

  useEffect(() => {
    const paramPlanId = checkoutFlow.isPeriodYearly
      ? process.env.REACT_APP_ENVF === 'env.production' ? checkoutFlow.planDef.yearlyPlanId : checkoutFlow.planDef.yearlyTestPlanId
      : process.env.REACT_APP_ENVF === 'env.production' ? checkoutFlow.planDef.monthlyPlanId : checkoutFlow.planDef.monthlyTestPlanId;
    log('[checkoutPlan]useEffect paramPlanId: ', paramPlanId);
    SPBackend.getPlanInfo(paramPlanId, user)
      .then(response => {
        log('plan info: ', response.data);
        const { plan, product } = response.data;
        setPlanInfo(plan);
      })
      .catch(err => {
        dispatch(setSnackMessage({
          message: err.message,
          level: 'error',
          duration: 5000
        }));
      });

    // Check if user already has a payment method
    SPBackend.getPaymentMethods(user)
      .then(response => {
        log('[getPaymentMethods] response: ', response);
        const {
          paymentMethods,
          defaultPaymentMethod
        } = response.data;
        setPaymentMethods(paymentMethods);
        setDefaultPaymentMethod(defaultPaymentMethod);
        if (typeof paymentMethods !== 'undefined' && paymentMethods !== null && paymentMethods.length > 0) {
          setShowAddNewCardForm(false);
        } else {
          setShowAddNewCardForm(true);
        }
      })
      .catch(e => {
        log('[getPaymentMethods] error: ', e);
        setShowAddNewCardForm(false);
      });
  }, [checkoutFlow, user, dispatch]);
  
  return (
    <Grid sx={classes.contentCtr}
      item
      xs={12}
    >
      <Box sx={classes.checkoutColCtr}>
        <Box sx={classes.checkoutContentHeader}>
          <Typography variant='h3'>Finalize & confirm</Typography>
        </Box>
        <Box sx={classes.checkoutLineCtr}>
          <Hidden only={['xs']}>
            <ContentfuRenderPlanDesc {...props} />
          </Hidden>
          <Box sx={classes.checkoutColCtr}>
            <Box sx={{...classes.reviewSectionCtr, ...classes.spaceTop}}>
              <Typography sx={{...classes.spaceTop}}
                variant='body1'
              ><b>Review details</b></Typography>
              <Box sx={{...classes.reviewItemCtr, ...classes.reviewItem, ...classes.pmListItem}}>
                <Typography variant='body1'>{checkoutFlow.planDef.title} plan</Typography>
                <Typography sx={{...classes.bottomText}}
                            className="price"
                            variant='body1'
                >{checkoutFlow.isPeriodYearly ? `$${numberWithCommas(checkoutFlow.planDef.yearly * 12)} per year` : `$${checkoutFlow.planDef.monthly} per month`}</Typography>
              </Box>

                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    // coupon={coupon} // FIXME
                    checkoutFlow={checkoutFlow}
                    defaultPaymentMethod={defaultPaymentMethod}
                    paymentMethods={paymentMethods}
                    planinfo={planInfo}
                    setCheckoutFlow={setCheckoutFlow}
                    setOpenDialog={setOpenDialog}
                    showAddNewCardForm={showAddNewCardForm}
                  />
                </Elements>
            </Box>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

type Props = {
  classes: Object,
  setOpen: Function,
  open: boolean,
  triggerAction: Function
};

// eslint-disable-next-line react/no-multi-comp
const UpgradePlanDialog = (props: Props) => {
  const emptyCheckoutFlowState = {
    currentStep: 0, // 0: pick a plan; 1: payment; 2: success!
    selectedPlanId: null
  };
  const {
    setOpen,
    open,
    triggerAction
  } = props;
  
  // Use hooks
  const theme = useTheme();
  const classes = createClasses(upgradePlanDialogStyles, theme);

  // Use selector instead of useStore
  const user = useSelector(selectUser);
  
  const [checkoutFlow, setCheckoutFlow] = useState(emptyCheckoutFlowState);

  const handleSelectPlan = (
    planId,  // stripe plan id
    isPeriodYearly,
    planDef, 
    planIndex,
    plan, 
    pricingTable) => {
      console.log('[handleSelectPlan] isPeriodYearly, planDef, plan, pricingTablePlans: ', isPeriodYearly,
        planDef, 
        planIndex,
        plan, 
        pricingTable);
    setCheckoutFlow((prev) => {
      return {
        ...prev,
        currentStep: prev.currentStep + 1,
        selectedPlanId: planId,
        planId,
        isPeriodYearly,
        planDef, 
        planIndex,
        plan, // FIXME: to deprecate - prop not used.
        pricingTable
      };
    });
  };

  const emptyCheckoutFlow = () => {
    setTimeout(() => {
      setCheckoutFlow(emptyCheckoutFlowState);
    }, 300);
  };

  return (
    <Dialog aria-labelledby='form-confirm-topup'
      sx={classes.dialogCtr}
      onClose={() => {setOpen(false); emptyCheckoutFlow();}}
      open={open || false}
    >
      <DialogContent sx={{...classes.grow, ...classes.flexContainer}}>
      {
        checkoutFlow.currentStep === 0
        ? <ContentfulProvider>
            <PlansPicker
              selectPlan={handleSelectPlan}
            />
          </ContentfulProvider>
        : null
      }
      {
        checkoutFlow.currentStep === 1
        ? <CheckoutPlan
          checkoutFlow={checkoutFlow}
          setCheckoutFlow={setCheckoutFlow}
          setOpenDialog={setOpen}
          />
        : null
      }
      {
        checkoutFlow.currentStep === 2
        ? <Grid sx={classes.contentCtr}
            item
            xs={12}
          >
            <Box sx={classes.checkoutColCtr}>
              <Box sx={classes.checkoutContentHeader}>
                <Typography variant='h3'>Success! You're ready to text 🤗</Typography>
              </Box>
              <Box sx={classes.checkoutColCtr}>
                <Typography sx={classes.captionText}
                  variant='body1'
                >Your invoice is available in the Billing section.</Typography>
                <Box sx={classes.spaceTop} />
                <Button
                  color='primary'
                  onClick={() => {setOpen(false); emptyCheckoutFlow();}}
                  style={{marginTop: 16}}
                  variant='contained'
                >Got it</Button>
                <Box sx={classes.spaceTop} />
              </Box>
            </Box>
          </Grid>
        : null
      }
      
      </DialogContent>
    </Dialog>
  );
};

export default UpgradePlanDialog;
